import { forwardRef, useMemo } from 'react'
import { useSearchParams } from 'react-router-dom'

import { ISingleContentBlockRichText } from '@typings'
import { highlightHtmlContent } from '@utilities/helpers/highlight-html-content'

import styles from './ac-richtext.module.scss'
import { Box } from '@mui/material'
import { AcRichTextLeftRight } from '../../features/ac-rich-text-left-right/ac-rich-text-left-right'
import { AcContainer } from '../ac-container/ac-container'

interface IAcRichText extends ISingleContentBlockRichText {
  home?: boolean
}

export const AcRichText = forwardRef(
  (
    { content, layout, image, embed, home = false }: IAcRichText,
    ref: React.ForwardedRef<HTMLElement>,
  ) => {
    if (!content) return null

    const [searchParams] = useSearchParams()
    const highlightTerm = searchParams.get('highlight') || ''

    const renderContent = useMemo(() => {
      // Apply safe HTML-aware highlighting that preserves HTML structure
      const highlightedContent = highlightTerm
        ? highlightHtmlContent({
            searchTerm: highlightTerm,
            content: content,
          })
        : content

      const newContent = (
        <article
          ref={ref}
          className={styles['ac-richtext']}
          dangerouslySetInnerHTML={{ __html: highlightedContent }}
        />
      )

      switch (layout) {
        case 'right-image':
          return (
            <AcRichTextLeftRight
              content={newContent}
              image={image}
              reverse
            />
          )

        case 'left-image':
          return (
            <AcRichTextLeftRight
              content={newContent}
              image={image}
            />
          )

        case 'right-embed':
          return (
            <AcRichTextLeftRight
              content={newContent}
              embed={embed}
              reverse
            />
          )

        case 'left-embed':
          return (
            <AcRichTextLeftRight
              content={newContent}
              embed={embed}
            />
          )

        default:
          return newContent
      }
    }, [layout, content, image, ref, embed, highlightTerm])

    if (home) {
      return (
        <Box
          sx={{
            paddingBlock: '4rem',
          }}>
          <AcContainer maxWidth={1152}>{renderContent}</AcContainer>
        </Box>
      )
    }

    return renderContent
  },
)
